import axios from "axios";
import { ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip } from 'chart.js';
import { getApiV1Url } from './config/api';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Bar } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import "./styles/App.css";
import "./styles/CertificatesPage.css"
import Certificates from "./components/Certificates/Certificates";
import { getPdfButtonLabel, useRegistryPdfExport } from "./hooks/useRegistryPdfExport";
import {
    buildCertificateRegistryCard,
    buildCertificatesSummary,
    describeCertificateFilters,
} from "./utils/certificatesPdfUtils";
import CertificatesFilter from "./components/Filters/CertificatesFilter";
import UniversalPagination from "./components/UniversalPagination";
import { ResponsiveBar } from '@nivo/bar';
import React from 'react';
import { ResponsivePie } from '@nivo/pie';
ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend, ChartDataLabels);

const APTS_COMMENT_COLORS = {
    base: [
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(255, 166, 0, 1)',
    ],
    dimmed: [
        'rgba(75, 192, 192, 0.2)',
        'rgba(255, 99, 132, 0.2)',
        'rgba(255, 206, 86, 0.2)',
    ]
};

const CERT_STATUS_COLORS = {
    base: [
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(247, 0, 150, 1)',
        'rgba(0, 0, 0, 1)',
        'rgba(255, 166, 0, 1)',
    ],
    dimmed: [
        'rgba(34, 197, 94, 0.2)',
        'rgba(239, 68, 68, 0.2)',
        'rgba(168, 85, 247, 0.2)',
        'rgba(59, 130, 246, 0.2)',
        'rgba(249, 115, 22, 0.2)',
    ]
};

export interface Certif {
    cert_reg_number: string;
    comment_apts: string;
    cert_link: string;
    cert_schema: string;
    cert_status: string;
    cert_reg_data: string;
    cert_finish_data: string;
    cert_stop_data: string | null;
    cert_stop_reason: string;
    applicant_inn: string;
    applicant_type: string;
    applicant_ogrn: string;
    applicant_fullname: string;
    applicant_shortname: string;
    applicant_adres: string;
    manufacture_inn: string;
    manufacture_fullname: string;
    manufacture_adres: string;
    manufacture_ogrn: string;
    manufacture_workplace: string;
    product_fullname: string;
    technical_docs: string;
    regulatory_documentation: string;
    lab_number_1: string;
    lan_name_1: string;
    lab_number_2: string;
    lan_name_2: string;
    lab_number_3: string;
    lan_name_3: string;
    os_number: string;
    os_name: string;
    os_id_fsa: string;
    os_ogrn: string;
    os_status: string;
    os_stop_data: string;
    smk_cert_number: string;
    smk_os_number: string;
    smk_os_name: string;
    cert_docs: string;
    cert_violation: string;
    lab_violation: string;
    os_violation: string;
    smk_violation: string;
    full_comment_apts: string;
    signature_person: string;
    product_import: string;
    custom_doc_type: string;
    number_custom_doc: string;
    custom_rejection: string;
    last_data_protocol: string;
    exam_manufacture_start_data: string;
    exam_manufacture_stop_data: string;
    sampling_data: string | null;
    decision_data: string;
    taken_simpling_data: string | null;
    taken_place: string;
    exam_expert: string;
    all_expert: string;
    date_update: string;
    applicant_region: string;
    region_manufacturer: string;
}

interface CertificatesPageProps {
    isFilterOpen?: boolean;
    setIsFilterOpen?: (open: boolean) => void;
}

function CertificatesPage({ isFilterOpen: propIsFilterOpen, setIsFilterOpen: propSetIsFilterOpen }: CertificatesPageProps) {
    const navigate = useNavigate();
    const contentRef = useRef<HTMLDivElement>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [certif, setCertif] = useState<Certif[]>([]);
    const [filteredCertif, setFilteredCertif] = useState<Certif[]>([]);

    const [filteredAptsComments, setFilteredAptsComments] = useState<string[]>([]);
    const [filteredCertStatuses, setFilteredCertStatuses] = useState<string[]>([]);
    const [filteredYears, setFilteredYears] = useState<string[]>([]);

    // Окно с выбором видимости диаграмм
    const [chartVisibility, setChartVisibility] = useState({
        aptsCommentDynamics: true,
        certStatusDynamics: true,
        osDynamics: true,
        labDynamics: true,
        applicantDynamics: true,
        manufacturerDynamics: true,
    });
    const [isChartPanelOpen, setIsChartPanelOpen] = useState<boolean>(true);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [sortField, setSortField] = useState<keyof Certif | ''>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const [viewportWidth, setViewportWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
    useEffect(() => {
        const onResize = () => setViewportWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const [filters, setFilters] = useState({
        cert_reg_number: [] as string[],
        os_name: [] as string[],
        os_number: [] as string[],
        applicant_shortname: [] as string[],
        manufacture_inn: [] as string[],
        cert_reg_data: [] as string[],
        manufacture_fullname: [] as string[],
        applicant_inn: [] as string[],
        applicant_region: [] as string[],
        region_manufacturer: [] as string[]
    });

    const { pdfProgress, startPdfExport, pdfReport } = useRegistryPdfExport();

    const [localIsFilterOpen, localSetIsFilterOpen] = useState(false);
    const isFilterOpen = propIsFilterOpen !== undefined ? propIsFilterOpen : localIsFilterOpen;
    const setIsFilterOpen = propSetIsFilterOpen !== undefined ? propSetIsFilterOpen : localSetIsFilterOpen;

    useEffect(() => {
        if (isFilterOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isFilterOpen) {
                setIsFilterOpen(false);
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleEsc);
        };
    }, [isFilterOpen, setIsFilterOpen]);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredCertif.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newItemsPerPage = parseInt(e.target.value);
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    const handleAptsCommentChartClick = (event: any, elements: any) => {
        if (elements && elements.length > 0) {
            const index = elements[0].index;
            const comment = getAptsCommentsData.labels[index];
            setFilteredAptsComments(prev => {
                const newComments = prev.includes(comment)
                    ? prev.filter(c => c !== comment)
                    : [...prev, comment];
                return newComments;
            });
        }
    };

    const handleCertStatusChartClick = (event: any, elements: any) => {
        if (elements && elements.length > 0) {
            const index = elements[0].index;
            const status = getCertStatusData().labels[index];
            setFilteredCertStatuses(prev => {
                const newStatuses = prev.includes(status)
                    ? prev.filter(s => s !== status)
                    : [...prev, status];
                return newStatuses;
            });
        }
    };

    const handleDynamicsChartClick = (event: any, elements: any) => {
        if (elements && elements.length > 0) {
            const index = elements[0].index;
            const year = getDynamicsData().labels[index];
            setFilteredYears(prev => {
                const newYears = prev.includes(year)
                    ? prev.filter(y => y !== year)
                    : [...prev, year];
                return newYears;
            });
        }
    };

    const handleSort = (field: keyof Certif) => {
        let newDirection: 'asc' | 'desc';
        let newField: keyof Certif | '';

        if (sortField === field) {
            if (sortDirection === 'asc') {
                newDirection = 'desc';
                newField = field;
            } else {
                newDirection = 'asc';
                newField = '';
            }
        } else {
            newDirection = 'asc';
            newField = field;
        }

        setSortField(newField);
        setSortDirection(newDirection);

        if (newField === '') {
            setFilteredCertif(certif);
        } else {
            const sortedList = [...filteredCertif].sort((a, b) => {
                const aValue = a[field] ?? '';
                const bValue = b[field] ?? '';
                if (aValue < bValue) return newDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return newDirection === 'asc' ? 1 : -1;
                return 0;
            });
            setFilteredCertif(sortedList);
        }
    };

    const getSortIcon = (field: keyof Certif) => {
        if (sortField !== field) return '↕';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const handleOpenCertDetails = useCallback((cert: Certif) => {
        if (!cert.cert_reg_number) {
            return;
        }
        const encodedNumber = encodeURIComponent(cert.cert_reg_number);
        navigate(`/certificates/${encodedNumber}`, { state: { cert } });
    }, [navigate]);

    const handleFilterChange = (newFilters: typeof filters) => {
        setFilters(newFilters);
    };

    const handleChartFilterChange = (newFilters: {
        aptsComments: string[];
        certStatuses: string[];
        years: string[];
    }) => {
        setFilteredAptsComments(newFilters.aptsComments);
        setFilteredCertStatuses(newFilters.certStatuses);
        setFilteredYears(newFilters.years);
    };

    

    const handleDownload = () => startPdfExport(() => {
        const selectedChartsCount = Object.values(chartVisibility).filter(Boolean).length;
        const filterDescription = describeCertificateFilters(filteredAptsComments, filteredCertStatuses, filteredYears, filters);
        return {
            fileName: 'certificates_report.pdf',
            summary: buildCertificatesSummary(filteredCertif, {
                title: 'Отчёт по реестру обязательных сертификатов',
                statusTitle: 'Статус сертификата в реестре обязательных сертификатов соответствия ФСА',
                filterDescription: `${filterDescription} · выбрано диаграмм: ${selectedChartsCount} из ${Object.keys(chartVisibility).length}`,
                showEmptyStatuses: true,
                showCommentDynamics: chartVisibility.aptsCommentDynamics,
                showStatusDynamics: chartVisibility.certStatusDynamics,
                showCertificationBodies: chartVisibility.osDynamics,
                showLaboratories: chartVisibility.labDynamics,
                showApplicants: chartVisibility.applicantDynamics,
                showManufacturers: chartVisibility.manufacturerDynamics,
            }),
            sections: [{ kind: 'cards', title: 'Реестр сертификатов', cards: filteredCertif.map(buildCertificateRegistryCard) }],
        };
    });



    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const access = localStorage.getItem('access');
                const response = await axios.get(getApiV1Url('CERTIFICATES'), {
                    headers: access ? { Authorization: `Bearer ${access}` } : {},
                    timeout: 30000
                });
                setCertif(response.data);
                setFilteredCertif(response.data);
            } catch (err: any) {
                if (err.code === 'ECONNABORTED') {
                    setError('Превышено время ожидания ответа от сервера');
                } else {
                    setError('Ошибка при загрузке данных');
                }
                console.error('Ошибка загрузки:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const filteredCertifMemo = useMemo(() => {
        const normalizeText = (text: string) => text?.toLowerCase().trim() || '';

        let filteredData = [...certif];

        if (filteredYears.length > 0) {
            filteredData = filteredData.filter(item =>
                item.cert_reg_data && filteredYears.includes(item.cert_reg_data.slice(0, 4))
            );
        }

        filteredData = filteredData.filter(cert => {
            const matchesAptsComment = filteredAptsComments.length === 0 ||
                filteredAptsComments.some(comment =>
                    normalizeText(cert.comment_apts) === normalizeText(comment)
                );

            const matchesCertStatus = filteredCertStatuses.length === 0 ||
                filteredCertStatuses.some(status =>
                    normalizeText(cert.cert_status) === normalizeText(status)
                );

            const matchesFilters = Object.entries(filters).every(([key, values]) => {
                if (values.length === 0) return true;
                if (key === 'lab_names') {
                    const candidates = [cert.lan_name_1, cert.lan_name_2, cert.lan_name_3]
                        .map(v => (v || '').toLowerCase().trim())
                        .filter(name => name && name !== 'отсутствует');
                    return (values as string[]).some(v => {
                        const needle = v.toLowerCase().trim();
                        return candidates.some(c => c.includes(needle));
                    });
                }
                const certValue = cert[key as keyof Certif]?.toString() || '';
                return (values as string[]).some(value =>
                    certValue.toLowerCase().includes(value.toLowerCase())
                );
            });

            return matchesAptsComment && matchesCertStatus && matchesFilters;
        });

        return filteredData;
    }, [certif, filteredAptsComments, filteredCertStatuses, filteredYears, filters]);

    useEffect(() => {
        setFilteredCertif(filteredCertifMemo);
        setCurrentPage(1);
    }, [filteredCertifMemo]);

    const getAptsCommentsData = useMemo(() => {
        const filteredCertificates = filteredCertifMemo.filter(cert => {
            const matchesCertStatus = filteredCertStatuses.length === 0 ||
                filteredCertStatuses.some(status =>
                    cert.cert_status?.toLowerCase() === status.toLowerCase()
                );

            const matchesYear = filteredYears.length === 0 ||
                filteredYears.some(year =>
                    cert.cert_reg_data?.substring(0, 4) === year
                );

            const matchesFilters = Object.entries(filters).every(([key, values]) => {
                if (values.length === 0) return true;
                const certValue = cert[key as keyof Certif]?.toString() || '';
                return values.some(value =>
                    certValue.toLowerCase().includes(value.toLowerCase())
                );
            });

            return matchesCertStatus && matchesYear && matchesFilters;
        });

        const noViolations = filteredCertificates.filter(cert => cert.comment_apts === "нарушений нет").length;
        const hasViolations = filteredCertificates.filter(cert => cert.comment_apts === "есть нарушения").length;
        const attention = filteredCertificates.filter(cert => cert.comment_apts === "обратить внимание").length;

        const colors = filteredAptsComments.length === 0 ? APTS_COMMENT_COLORS.base :
            APTS_COMMENT_COLORS.base.map((color, index) =>
                filteredAptsComments.includes(['Нарушений нет', 'Есть нарушения', 'Обратить внимание'][index])
                    ? color
                    : APTS_COMMENT_COLORS.dimmed[index]
            );

        return {
            labels: ['Нарушений нет', 'Есть нарушения', 'Обратить внимание'],
            datasets: [
                {
                    data: [noViolations, hasViolations, attention],
                    backgroundColor: colors,
                    borderWidth: 1,
                },
            ],
        };
    }, [filteredCertifMemo, filteredCertStatuses, filteredYears, filters, filteredAptsComments]);

    const getCertStatusData = () => {
        const filteredCertificates = certif.filter(cert => {
            const matchesAptsComment = filteredAptsComments.length === 0 ||
                filteredAptsComments.some(comment =>
                    cert.comment_apts?.toLowerCase() === comment.toLowerCase()
                );

            const matchesYear = filteredYears.length === 0 ||
                filteredYears.some(year =>
                    cert.cert_reg_data?.substring(0, 4) === year
                );

            const matchesFilters = Object.entries(filters).every(([key, values]) => {
                if (values.length === 0) return true;
                const certValue = cert[key as keyof Certif]?.toString() || '';
                return values.some(value =>
                    certValue.toLowerCase().includes(value.toLowerCase())
                );
            });

            return matchesAptsComment && matchesYear && matchesFilters;
        });

        const active = filteredCertificates.filter(cert =>
            cert.cert_status &&
            (cert.cert_status.toLowerCase() === "действует" ||
                cert.cert_status.toLowerCase() === "действующий")
        ).length;

        const terminated = filteredCertificates.filter(cert =>
            cert.cert_status &&
            (cert.cert_status.toLowerCase() === "прекращен" ||
                cert.cert_status.toLowerCase() === "прекращенный")
        ).length;

        const archived = filteredCertificates.filter(cert =>
            cert.cert_status &&
            (cert.cert_status.toLowerCase() === "архивный" ||
                cert.cert_status.toLowerCase() === "архивирован")
        ).length;

        const invalid = filteredCertificates.filter(cert =>
            cert.cert_status &&
            (cert.cert_status.toLowerCase() === "недействителен" ||
                cert.cert_status.toLowerCase() === "недействительный")
        ).length;

        const suspended = filteredCertificates.filter(cert =>
            cert.cert_status &&
            (cert.cert_status.toLowerCase() === "приостановлен" ||
                cert.cert_status.toLowerCase() === "приостановленный")
        ).length;

        const colors = filteredCertStatuses.length === 0 ? CERT_STATUS_COLORS.base :
            CERT_STATUS_COLORS.base.map((color, index) =>
                filteredCertStatuses.includes(['Действует', 'Прекращен', 'Архивный', 'Недействителен', 'Приостановлен'][index])
                    ? color
                    : CERT_STATUS_COLORS.dimmed[index]
            );

        return {
            labels: ['Действует', 'Прекращен', 'Архивный', 'Недействителен', 'Приостановлен'],
            datasets: [
                {
                    data: [active, terminated, archived, invalid, suspended],
                    backgroundColor: colors,
                    borderColor: CERT_STATUS_COLORS.base,
                    borderWidth: 1,
                },
            ],
        };
    };

    const getDynamicsData = () => {
        const filteredCertificates = certif.filter(cert => {
            const matchesAptsComment = filteredAptsComments.length === 0 ||
                filteredAptsComments.some(comment =>
                    cert.comment_apts?.toLowerCase() === comment.toLowerCase()
                );

            const matchesCertStatus = filteredCertStatuses.length === 0 ||
                filteredCertStatuses.some(status =>
                    cert.cert_status?.toLowerCase() === status.toLowerCase()
                );

            const matchesFilters = Object.entries(filters).every(([key, values]) => {
                if (values.length === 0) return true;
                const certValue = cert[key as keyof Certif]?.toString() || '';
                return values.some(value =>
                    certValue.toLowerCase().includes(value.toLowerCase())
                );
            });

            return matchesAptsComment && matchesCertStatus && matchesFilters;
        });

        const years = ['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025'];

        const dataByYear = years.map(year => {
            const yearCertificates = filteredCertificates.filter(cert => {
                const certYear = cert.cert_reg_data ? cert.cert_reg_data.substring(0, 4) : '';
                return certYear === year;
            });

            return {
                year,
                noViolations: yearCertificates.filter(cert => cert.comment_apts === "нарушений нет").length,
                hasViolations: yearCertificates.filter(cert => cert.comment_apts === "есть нарушения").length,
                attention: yearCertificates.filter(cert => cert.comment_apts === "обратить внимание").length
            };
        });

        return {
            labels: years,
            datasets: [
                {
                    label: 'Нарушений нет',
                    data: dataByYear.map(item => item.noViolations),
                    backgroundColor: 'rgba(126, 178, 70, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Есть нарушения',
                    data: dataByYear.map(item => item.hasViolations),
                    backgroundColor: 'rgba(237, 93, 98, 1)',
                    borderWidth: 1
                },
                {
                    label: 'Обратить внимание',
                    data: dataByYear.map(item => item.attention),
                    backgroundColor: 'rgba(255, 166, 0, 1)',
                    borderWidth: 1
                }
            ]
        };
    };


    const nivoKeys = ['Нарушений нет', 'Есть нарушения', 'Обратить внимание'];
    const getNivoBarData = () => {
        const yearsSet = new Set<string>();
        filteredCertif.forEach(item => {
            const year = item.cert_reg_data ? String(item.cert_reg_data.substring(0, 4)) : '';
            if (year) yearsSet.add(year);
        });
        let years: string[] = Array.from(yearsSet).map(String).sort();
        if (filteredYears.length > 0) {
            years = years.filter(y => filteredYears.includes(String(y)));
        }
        return years.map((year: string) => {
            const row: Record<string, number | string> = { year: String(year) };
            nivoKeys.forEach((key: string) => {
                let count = 0;
                if (key === 'Нарушений нет') {
                    count = filteredCertif.filter(item => item.cert_reg_data && String(item.cert_reg_data.substring(0, 4)) === String(year) && item.comment_apts === 'нарушений нет').length;
                } else if (key === 'Есть нарушения') {
                    count = filteredCertif.filter(item => item.cert_reg_data && String(item.cert_reg_data.substring(0, 4)) === String(year) && item.comment_apts === 'есть нарушения').length;
                } else if (key === 'Обратить внимание') {
                    count = filteredCertif.filter(item => item.cert_reg_data && String(item.cert_reg_data.substring(0, 4)) === String(year) && item.comment_apts === 'обратить внимание').length;
                }
                row[key] = count;
            });
            return row;
        });
    };

    const certStatusNivoKeys = ['Действует', 'Прекращен', 'Архивный', 'Недействителен', 'Приостановлен'];
    const getCertStatusNivoBarData = () => {
        const yearsSet = new Set<string>();
        filteredCertif.forEach(item => {
            const year = item.cert_reg_data ? String(item.cert_reg_data.substring(0, 4)) : '';
            if (year) yearsSet.add(year);
        });
        let years: string[] = Array.from(yearsSet).map(String).sort();
        if (filteredYears.length > 0) {
            years = years.filter(y => filteredYears.includes(String(y)));
        }
        return years.map((year: string) => {
            const row: Record<string, number | string> = { year: String(year) };
            certStatusNivoKeys.forEach((key: string) => {
                let count = 0;
                if (key === 'Действует') {
                    count = filteredCertif.filter(item => 
                        item.cert_reg_data && 
                        String(item.cert_reg_data.substring(0, 4)) === String(year) && 
                        item.cert_status && 
                        (item.cert_status.toLowerCase() === "действует" || item.cert_status.toLowerCase() === "действующий")
                    ).length;
                } else if (key === 'Прекращен') {
                    count = filteredCertif.filter(item => 
                        item.cert_reg_data && 
                        String(item.cert_reg_data.substring(0, 4)) === String(year) && 
                        item.cert_status && 
                        (item.cert_status.toLowerCase() === "прекращен" || item.cert_status.toLowerCase() === "прекращенный")
                    ).length;
                } else if (key === 'Архивный') {
                    count = filteredCertif.filter(item => 
                        item.cert_reg_data && 
                        String(item.cert_reg_data.substring(0, 4)) === String(year) && 
                        item.cert_status && 
                        (item.cert_status.toLowerCase() === "архивный" || item.cert_status.toLowerCase() === "архивирован")
                    ).length;
                } else if (key === 'Недействителен') {
                    count = filteredCertif.filter(item => 
                        item.cert_reg_data && 
                        String(item.cert_reg_data.substring(0, 4)) === String(year) && 
                        item.cert_status && 
                        (item.cert_status.toLowerCase() === "недействителен" || item.cert_status.toLowerCase() === "недействительный")
                    ).length;
                } else if (key === 'Приостановлен') {
                    count = filteredCertif.filter(item => 
                        item.cert_reg_data && 
                        String(item.cert_reg_data.substring(0, 4)) === String(year) && 
                        item.cert_status && 
                        (item.cert_status.toLowerCase() === "приостановлен" || item.cert_status.toLowerCase() === "приостановленный")
                    ).length;
                }
                row[key] = count;
            });
            return row;
        });
    };
    const nivoColors = [
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(255, 166, 0, 1)',
    ];

    const seriesColors = [
        'rgba(126, 178, 70, 1)', 
        'rgba(237, 93, 98, 1)',    
        'rgba(255, 166, 0, 1)',    
        '#1f77b4',                 
        '#9467bd',                
        '#2ca02c',                 
        '#ff7f0e',                
        '#17becf',               
        '#8c564b',                 
        '#e377c2',                 
        '#7f7f7f',                 
        '#bcbd22',                 
    ];

    const certStatusNivoColors = [
        'rgba(126, 178, 70, 1)',  
        'rgba(237, 93, 98, 1)',   
        'rgba(247, 0, 150, 1)',   
        'rgba(0, 0, 0, 1)',       
        'rgba(255, 166, 0, 1)',  
    ];

    const applicantMaps = useMemo(() => {
        const nameToInn = new Map<string, string>();
        const innToName = new Map<string, string>();
        const normalize = (s: string) => s.replace(/\s+/g, ' ').trim();
        for (const item of filteredCertif) {
            const inn = normalize(item.applicant_inn || '');
            if (!inn) continue;
            const full = normalize(item.applicant_fullname || '');
            const short = normalize(item.applicant_shortname || '');
            const name = short || full;
            if (!name || name.toLowerCase() === 'отсутствует' || /^\d{10,}$/.test(name)) continue;
            if (!innToName.has(inn)) innToName.set(inn, name);
            if (!nameToInn.has(name)) nameToInn.set(name, inn);
        }
        return { nameToInn, innToName };
    }, [filteredCertif]);

    const manufacturerMaps = useMemo(() => {
        const nameToInn = new Map<string, string>();
        const innToName = new Map<string, string>();
        for (const item of filteredCertif) {
            const inn = (item.manufacture_inn || '').trim();
            const name = (item.manufacture_fullname || '').trim();
            if (!inn || !name || name.toLowerCase() === 'отсутствует' || /^\d{10,}$/.test(name)) continue;
            if (!innToName.has(inn)) innToName.set(inn, name);
            if (!nameToInn.has(name)) nameToInn.set(name, inn);
        }
        return { nameToInn, innToName };
    }, [filteredCertif]);

    const getOSDynamicsData = () => {
        const osCounts = new Map<string, Map<string, number>>();
        filteredCertif.forEach(item => {
            if (!item.os_name || !item.cert_reg_data) return;
            const year = String(item.cert_reg_data.substring(0, 4));
            if (!osCounts.has(year)) osCounts.set(year, new Map());
            const m = osCounts.get(year)!;
            const osKey = item.os_number ? `${item.os_name} (${item.os_number})` : item.os_name;
            m.set(osKey, (m.get(osKey) || 0) + 1);
        });
        const osNames = Array.from(new Set(Array.from(osCounts.values()).flatMap(m => Array.from(m.keys())))).sort();
        const years = Array.from(new Set(filteredCertif.map(i => i.cert_reg_data ? i.cert_reg_data.substring(0,4) : '').filter(Boolean))).sort();
        return { years, osNames, osCounts };
    };

    const getLabDynamicsData = () => {
        const labCounts = new Map<string, Map<string, number>>();
        const allLabs = new Set<string>();
        filteredCertif.forEach(item => {
            if (!item.cert_reg_data) return;
            const year = String(item.cert_reg_data.substring(0, 4));
            if (!labCounts.has(year)) labCounts.set(year, new Map());
            const m = labCounts.get(year)!;
            [item.lan_name_1, item.lan_name_2, item.lan_name_3].forEach(name => {
                const n = (name || '').trim();
                if (n && n.toLowerCase() !== 'отсутствует') {
                    m.set(n, (m.get(n) || 0) + 1);
                    allLabs.add(n);
                }
            });
        });
        const labNames = Array.from(allLabs).sort();
        const years = Array.from(new Set(filteredCertif.map(i => i.cert_reg_data ? i.cert_reg_data.substring(0,4) : '').filter(Boolean))).sort();
        return { years, labNames, labCounts };
    };

    const getApplicantDynamicsData = () => {
        const applicantCounts = new Map<string, Map<string, number>>();
        filteredCertif.forEach(item => {
            if (!item.cert_reg_data) return;
            const year = String(item.cert_reg_data.substring(0, 4));
            if (!applicantCounts.has(year)) applicantCounts.set(year, new Map());
            const m = applicantCounts.get(year)!;
            const inn = (item.applicant_inn || '').trim();
            if (!inn) return;
            const shortname = (item.applicant_shortname || '').trim();
            const applicantKey = shortname ? `${inn} - ${shortname}` : inn;
            m.set(applicantKey, (m.get(applicantKey) || 0) + 1);
        });
        const applicantNames = Array.from(new Set(Array.from(applicantCounts.values()).flatMap(m => Array.from(m.keys())))).sort();
        const years = Array.from(new Set(filteredCertif.map(i => i.cert_reg_data ? i.cert_reg_data.substring(0,4) : '').filter(Boolean))).sort();
        return { years, applicantNames, applicantCounts };
    };

    const getManufacturerDynamicsData = () => {
        const manufacturerCounts = new Map<string, Map<string, number>>();
        filteredCertif.forEach(item => {
            if (!item.cert_reg_data) return;
            const year = String(item.cert_reg_data.substring(0, 4));
            if (!manufacturerCounts.has(year)) manufacturerCounts.set(year, new Map());
            const m = manufacturerCounts.get(year)!;
            const inn = (item.manufacture_inn || '').trim();
            if (!inn) return;
            let name = manufacturerMaps.innToName.get(inn);
            if (!name) {
                const rawName = (item.manufacture_fullname || '').trim();
                name = rawName && rawName.toLowerCase() !== 'отсутствует' && !/^\d{10,}$/.test(rawName) ? rawName : inn;
            }
            m.set(name, (m.get(name) || 0) + 1);
        });
        const manufacturerNames = Array.from(new Set(Array.from(manufacturerCounts.values()).flatMap(m => Array.from(m.keys())))).sort();
        const years = Array.from(new Set(filteredCertif.map(i => i.cert_reg_data ? i.cert_reg_data.substring(0,4) : '').filter(Boolean))).sort();
        return { years, manufacturerNames, manufacturerCounts };
    };

    const getIntegerTickValues = (maxValue: number): number[] => {
        if (!isFinite(maxValue) || maxValue <= 0) return [0];
        const niceStep = (n: number) => {
            const pow = Math.pow(10, Math.floor(Math.log10(n)));
            const d = n / pow;
            let step: number;
            if (d <= 1) step = 1;
            else if (d <= 2) step = 2;
            else if (d <= 5) step = 5;
            else step = 10;
            return step * pow;
        };
        const roughStep = Math.ceil(maxValue / 8);
        const step = Math.max(1, niceStep(roughStep));
        const upper = Math.ceil(maxValue / step) * step;
        const ticks: number[] = [];
        for (let v = 0; v <= upper; v += step) ticks.push(v);
        return ticks;
    };

    const getTickValuesByStep = (maxValue: number, step: number): number[] => {
        if (!isFinite(maxValue) || maxValue <= 0) return [0, step];
        const upper = Math.ceil(maxValue / step) * step;
        const ticks: number[] = [];
        for (let v = 0; v <= upper; v += step) ticks.push(v);
        return ticks;
    };

    const getUpperByStep = (maxValue: number, step: number): number => {
        if (!isFinite(maxValue) || maxValue <= 0) return step;
        return Math.ceil(maxValue / step) * step;
    };

    const getBarChartMaxTotal = (): number => {
        const data = getNivoBarData();
        return data.reduce((maxSoFar, row) => {
            const total = nivoKeys.reduce((sum, key) => sum + Number(row[key] || 0), 0);
            return Math.max(maxSoFar, total);
        }, 0);
    };

    const getCertStatusBarChartMaxTotal = (): number => {
        const data = getCertStatusNivoBarData();
        return data.reduce((maxSoFar, row) => {
            const total = certStatusNivoKeys.reduce((sum, key) => sum + Number(row[key] || 0), 0);
            return Math.max(maxSoFar, total);
        }, 0);
    };

    const dynamicsOptions = {
        responsive: true,
        onClick: handleDynamicsChartClick,
        scales: {
            y: {
                beginAtZero: true,
                max: 100,
                title: {
                    display: true,
                    text: 'Количество сертификатов'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Год'
                }
            }
        },
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    boxWidth: 15,
                    boxHeight: 15
                }
            },
            title: {
                display: true,
                text: 'Динамика'
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        const dataset = context.dataset;
                        const total = dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.dataset.label}: ${value} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                formatter: (value: number) => value,
                color: '#000',
                font: {
                    weight: 'bold' as const
                }
            }
        }
    };

    const aptsCommentOptions = {
        onClick: handleAptsCommentChartClick,
        plugins: {
            legend: {
                position: 'right' as const,
                labels: {
                    boxWidth: 15,
                    boxHeight: 15
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.label}: ${value} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                formatter: (value: number) => value,
                color: '#000',
                font: {
                    weight: 'bold' as const
                }
            }
        },
    };

    const certStatusOptions = {
        onClick: handleCertStatusChartClick,
        plugins: {
            legend: {
                position: 'right' as const,
                labels: {
                    boxWidth: 15,
                    boxHeight: 15
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const value = context.raw;
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${context.label}: ${value} (${percentage}%)`;
                    }
                }
            },
            datalabels: {
                formatter: (value: number) => value,
                color: '#000',
                font: {
                    weight: 'bold' as const
                }
            }
        },
    };

    const aptsCommentsPieData = getAptsCommentsData.labels
        .map((label: string, idx: number) => ({
            id: label,
            label,
            value: getAptsCommentsData.datasets[0].data[idx],
            color: getAptsCommentsData.datasets[0].backgroundColor[idx] || '#ccc',
        }))
        .filter((item: any) => item.value > 0);
    console.log('aptsCommentsPieData', aptsCommentsPieData);

    const certStatusPieData = getCertStatusData().labels
        .map((label, idx) => ({
            id: label,
            label,
            value: getCertStatusData().datasets[0].data[idx],
            color: getCertStatusData().datasets[0].backgroundColor[idx] || '#ccc',
        }))
        .filter(item => item.value > 0);
    console.log('certStatusPieData', certStatusPieData);

    function getAptsCommentsDataFiltered() {
        const noViolations = filteredCertif.filter(cert => cert.comment_apts === "нарушений нет").length;
        const hasViolations = filteredCertif.filter(cert => cert.comment_apts === "есть нарушения").length;
        const attention = filteredCertif.filter(cert => cert.comment_apts === "обратить внимание").length;
        const colors = ['rgba(126, 178, 70, 1)', 'rgba(237, 93, 98, 1)', 'rgba(255, 166, 0, 1)'];
        return {
            labels: ['Нарушений нет', 'Есть нарушения', 'Обратить внимание'],
            datasets: [
                {
                    data: [noViolations, hasViolations, attention],
                    backgroundColor: colors,
                    borderWidth: 1,
                },
            ],
        };
    }
    function getCertStatusDataFiltered() {
        const active = filteredCertif.filter(cert => cert.cert_status && (cert.cert_status.toLowerCase() === "действует" || cert.cert_status.toLowerCase() === "действующий")).length;
        const terminated = filteredCertif.filter(cert => cert.cert_status && (cert.cert_status.toLowerCase() === "прекращен" || cert.cert_status.toLowerCase() === "прекращенный")).length;
        const archived = filteredCertif.filter(cert => cert.cert_status && (cert.cert_status.toLowerCase() === "архивный" || cert.cert_status.toLowerCase() === "архивирован")).length;
        const invalid = filteredCertif.filter(cert => cert.cert_status && (cert.cert_status.toLowerCase() === "недействителен" || cert.cert_status.toLowerCase() === "недействительный")).length;
        const suspended = filteredCertif.filter(cert => cert.cert_status && (cert.cert_status.toLowerCase() === "приостановлен" || cert.cert_status.toLowerCase() === "приостановленный")).length;
        const colors = ['rgba(126, 178, 70, 1)', 'rgba(237, 93, 98, 1)', 'rgba(247, 0, 150, 1)', 'rgba(0, 0, 0, 1)', 'rgba(255, 166, 0, 1)'];
        return {
            labels: ['Действует', 'Прекращен', 'Архивный', 'Недействителен', 'Приостановлен'],
            datasets: [
                {
                    data: [active, terminated, archived, invalid, suspended],
                    backgroundColor: colors,
                    borderWidth: 1,
                },
            ],
        };
    }
    const aptsCommentsPieDataFiltered = getAptsCommentsDataFiltered().labels
        .map((label: string, idx: number) => ({
            id: label,
            label,
            value: getAptsCommentsDataFiltered().datasets[0].data[idx],
            color: String(getAptsCommentsDataFiltered().datasets[0].backgroundColor[idx] || '#ccc'),
            isSelected: filteredAptsComments.includes(label),
        }))
        .filter((item: any) => item.value > 0);
    console.log('aptsCommentsPieDataFiltered', aptsCommentsPieDataFiltered);

    const certStatusPieDataFiltered = getCertStatusDataFiltered().labels
        .map((label: string, idx: number) => ({
            id: label,
            label,
            value: getCertStatusDataFiltered().datasets[0].data[idx],
            color: String(getCertStatusDataFiltered().datasets[0].backgroundColor[idx] || '#ccc'),
            isSelected: filteredCertStatuses.includes(label),
        }))
        .filter((item: any) => item.value > 0);
    console.log('certStatusPieDataFiltered', certStatusPieDataFiltered);

    const handleAptsPieClick = (datum: any) => {
        const label = datum.id;
        let newApts = filteredAptsComments.includes(label)
            ? filteredAptsComments.filter((l: string) => l !== label)
            : [...filteredAptsComments, label];
        handleChartFilterChange({
            aptsComments: newApts,
            certStatuses: filteredCertStatuses,
            years: filteredYears,
        });
    };
    const handleCertStatusPieClick = (datum: any) => {
        const label = datum.id;
        let newStatuses = filteredCertStatuses.includes(label)
            ? filteredCertStatuses.filter((l: string) => l !== label)
            : [...filteredCertStatuses, label];
        handleChartFilterChange({
            aptsComments: filteredAptsComments,
            certStatuses: newStatuses,
            years: filteredYears,
        });
    };

    const getUniqueCertificationBodies = () => {
        const bodies = new Map();
        filteredCertif.forEach(cert => {
            if (cert.os_number && cert.os_name) {
                const key = `${cert.os_number}-${cert.os_name}`;
                if (!bodies.has(key)) {
                    bodies.set(key, {
                        number: cert.os_number,
                        name: cert.os_name,
                        status: cert.os_status || 'Не указан'
                    });
                }
            }
        });
        return Array.from(bodies.values());
    };

    const getUniqueLaboratories = () => {
        const labs = new Map();
        filteredCertif.forEach(cert => {
            const labFields = [
                { number: cert.lab_number_1, name: cert.lan_name_1 },
                { number: cert.lab_number_2, name: cert.lan_name_2 },
                { number: cert.lab_number_3, name: cert.lan_name_3 }
            ];

            labFields.forEach(field => {
                const name = (field.name || '').toString().trim();
                if (field.number && name && name.toLowerCase() !== 'отсутствует') {
                    const key = `${field.number}-${name}`;
                    if (!labs.has(key)) {
                        labs.set(key, {
                            number: field.number,
                            name
                        });
                    }
                }
            });
        });
        return Array.from(labs.values());
    };

    const getUniqueManufacturers = () => {
        const manufacturers = new Map();
        filteredCertif.forEach(cert => {
            if (cert.manufacture_inn && cert.manufacture_fullname) {
                const key = `${cert.manufacture_inn}-${cert.manufacture_fullname}`;
                if (!manufacturers.has(key)) {
                    manufacturers.set(key, {
                        inn: cert.manufacture_inn,
                        name: cert.manufacture_fullname
                    });
                }
            }
        });
        return Array.from(manufacturers.values());
    };

    const getUniqueApplicants = () => {
        const applicants = new Map();
        filteredCertif.forEach(cert => {
            if (cert.applicant_inn && cert.applicant_fullname) {
                const key = `${cert.applicant_inn}-${cert.applicant_fullname}`;
                if (!applicants.has(key)) {
                    applicants.set(key, {
                        inn: cert.applicant_inn,
                        name: cert.applicant_fullname
                    });
                }
            }
        });
        return Array.from(applicants.values());
    };

    const barColors = [
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(255, 166, 0, 1)',
        '#8884d8',
        '#82ca9d',
        'rgba(255, 166, 0, 1)',
        'rgba(126, 178, 70, 1)',
        'rgba(237, 93, 98, 1)',
        'rgba(255, 166, 0, 1)',
    ];

    if (loading) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999
        }}>
            <img
                src="/loading.png"
                alt="Загрузка..."
                style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
            />
            <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        </div>
    );

    if (error) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(255, 255, 255, 0.95)'
        }}>
            <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                maxWidth: '500px'
            }}>
                <h3 style={{ color: '#e74c3c', marginBottom: '16px' }}>Ошибка загрузки</h3>
                <p style={{ marginBottom: '20px', color: '#666' }}>{error}</p>
                <button
                    onClick={() => window.location.reload()}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#0071bc',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                >
                    Попробовать снова
                </button>
            </div>
        </div>
    );

    const noResults = filteredCertif.length === 0;

    return (
        <div ref={contentRef} className="certificates-container">

            <div className="navigation_certificates">
                <nav className="breadcrumbs">
                    <span
                        className="breadcrumb-home"
                        style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
                        onClick={() => navigate('/')}
                    >
                        Главная страница
                    </span>
                    <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
                    <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000' }}>
                        Реестр обязательных сертификатов
                    </span>
                </nav>
                <button className="download-button" onClick={handleDownload} disabled={pdfProgress !== null || filteredCertif.length === 0}>
                    {getPdfButtonLabel(pdfProgress)}
                </button>
            </div>
            {pdfReport}
            {isFilterOpen && (
                <div className="certificates-filter-modal-overlay">
                    <div className="certificates-filter-modal">
                        <button className="certificates-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
                            ×
                        </button>
                        <CertificatesFilter
                            certificates={certif}
                            onFilterChange={handleFilterChange}
                            chartFilters={{
                                aptsComments: filteredAptsComments,
                                certStatuses: filteredCertStatuses,
                                years: filteredYears
                            }}
                            onChartFilterChange={handleChartFilterChange}
                            initialFilters={filters}
                        />
                    </div>
                </div>
            )}

            {noResults ? (
                <div style={{
                    marginTop: 24,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    padding: 24,
                    textAlign: 'center',
                    color: '#374151'
                }}>
                    <h3 style={{ margin: 0, marginBottom: 8 }}>Нет результатов</h3>
                    <div>По выбранным фильтрам не найдено данных. Измените параметры и попробуйте снова.</div>
                </div>
            ) : (
            <div className="certificates-content">
                <div className="pdf-page" style={{ pageBreakAfter: 'always' }}>
                    <div className="certificates-stats-blocks">
                        <div className="certificates-stats-block">
                            <h3>Общее количество сертификатов</h3>
                            <h1 className="stats-value">{filteredCertif.length}</h1>
                        </div>
                        <div className="certificates-stats-block">
                            <h3>Общее количество органов по сертификации</h3>
                            <h1 className="stats-value">{getUniqueCertificationBodies().length}</h1>
                        </div>
                        <div className="certificates-stats-block">
                            <h3>Общее количество лабораторий</h3>
                            <h1 className="stats-value">{getUniqueLaboratories().length}</h1>
                        </div>
                    </div>
                    <div className="certificates-stats-blocks certificates-stats-blocks-bottom">
                        <div className="certificates-stats-block">
                            <h3>Общее количество изготовителей</h3>
                            <h1 className="stats-value">{getUniqueManufacturers().length}</h1>
                        </div>
                        <div className="certificates-stats-block">
                            <h3>Общее количество заявителей</h3>
                            <h1 className="stats-value">{getUniqueApplicants().length}</h1>
                        </div>
                    </div>

                    <div className="certificates-charts" style={{ display: 'flex', alignItems: 'stretch', gap: 32 }}>
                        <div className="certificates-count">
                            <h3>Общее количество сертификатов</h3>
                            <h1 className="count-value">{filteredCertif.length}</h1>
                        </div>
                        <div className="chart-container">
                            <h3>Комментарий АПТС</h3>
                            <div className="pie-chart" style={{ height: 250, width: 450, minWidth: 300, minHeight: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                                <div style={{ width: 450, height: 250 }}>
                                    {aptsCommentsPieDataFiltered.length > 0 ? (
                                        <ResponsivePie
                                            data={aptsCommentsPieDataFiltered}
                                            margin={{
                                                top: 20,
                                                right: viewportWidth <= 430 ? 200 : 270,
                                                bottom: 20,
                                                left: 20
                                            }}
                                            innerRadius={0.5}
                                            padAngle={0.7}
                                            cornerRadius={3}
                                            activeOuterRadiusOffset={16}
                                            colors={aptsCommentsPieDataFiltered.map(d => d.color)}
                                            borderWidth={1}
                                            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                            enableArcLabels={true}
                                            arcLabel={d => String(d.value)}
                                            enableArcLinkLabels={false}
                                            theme={{
                                                labels: {
                                                    text: {
                                                        fontWeight: 700,
                                                        fill: '#111',
                                                    },
                                                },
                                            }}
                                            onClick={handleAptsPieClick}
                                            activeId={filteredAptsComments.length > 0 ? filteredAptsComments[filteredAptsComments.length - 1] : undefined}
                                            legends={[{
                                                anchor: 'right',
                                                direction: 'column',
                                                justify: false,
                                                translateX: 130,
                                                translateY: 0,
                                                itemsSpacing: 4,
                                                itemWidth: 90,
                                                itemHeight: 18,
                                                itemTextColor: '#999',
                                                itemDirection: 'left-to-right',
                                                itemOpacity: 1,
                                                symbolSize: 15,
                                                symbolShape: 'circle',
                                                effects: [
                                                    {
                                                        on: 'hover',
                                                        style: {
                                                            itemTextColor: '#000',
                                                        },
                                                    },
                                                ],
                                            }]}
                                            tooltip={({ datum }) => {
                                                const total = aptsCommentsPieDataFiltered.reduce((sum, d) => sum + d.value, 0);
                                                const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                                                return (
                                                    <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}>
                                                        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{datum.label}</div>
                                                        <span style={{ color: datum.color, fontWeight: 700, marginRight: 8 }}>■</span>
                                                        <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                                                    </div>
                                                );
                                            }}
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет данных для отображения</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="chart-container">
                            <h3>Статус сертификата в реестре обязательных сертификатов соответствия ФСА​</h3>
                            <div className="pie-chart" style={{ height: 240, width: 450, minWidth: 220, minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
                                <div style={{ width: 450, height: 240 }}>
                                    {certStatusPieDataFiltered.length > 0 ? (
                                        <ResponsivePie
                                            data={certStatusPieDataFiltered}
                                            margin={{
                                                top: 20,
                                                right: viewportWidth <= 430 ? 200 : 270,
                                                bottom: 20,
                                                left: 20
                                            }}
                                            innerRadius={0.5}
                                            padAngle={0.7}
                                            cornerRadius={3}
                                            activeOuterRadiusOffset={16}
                                            colors={certStatusPieDataFiltered.map(d => d.color)}
                                            borderWidth={1}
                                            borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                            enableArcLabels={true}
                                            arcLabel={d => String(d.value)}
                                            enableArcLinkLabels={false}
                                            theme={{
                                                labels: {
                                                    text: {
                                                        fontWeight: 700,
                                                        fill: '#ffffff',
                                                    },
                                                },
                                            }}
                                            onClick={handleCertStatusPieClick}
                                            activeId={filteredCertStatuses.length > 0 ? filteredCertStatuses[filteredCertStatuses.length - 1] : undefined}
                                            legends={[{
                                                anchor: 'right',
                                                direction: 'column',
                                                justify: false,
                                                translateX: 130,
                                                translateY: 0,
                                                itemsSpacing: 4,
                                                itemWidth: 90,
                                                itemHeight: 18,
                                                itemTextColor: '#999',
                                                itemDirection: 'left-to-right',
                                                itemOpacity: 1,
                                                symbolSize: 15,
                                                symbolShape: 'circle',
                                                effects: [
                                                    {
                                                        on: 'hover',
                                                        style: {
                                                            itemTextColor: '#000',
                                                        },
                                                    },
                                                ],
                                            }]}
                                            tooltip={({ datum }) => {
                                                const total = certStatusPieDataFiltered.reduce((sum, d) => sum + d.value, 0);
                                                const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                                                return (
                                                    <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}>
                                                        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>{datum.label}</div>
                                                        <span style={{ color: datum.color, fontWeight: 700, marginRight: 8 }}>■</span>
                                                        <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                                                    </div>
                                                );
                                            }}
                                        />
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>Нет данных для отображения</div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <div style={{ 
                    padding: "12px 16px", 
                    marginBottom: "16px", 
                    background: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}>
                    <button
                        onClick={() => setIsChartPanelOpen(prev => !prev)}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px 2px'
                        }}
                    >
                        <span style={{ fontSize: '18px', fontWeight: 600 }}>Диаграммы ({Object.values(chartVisibility).filter(v => v).length}/{Object.keys(chartVisibility).length})</span>
                        <span style={{ transform: isChartPanelOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▼</span>
                    </button>
                    {isChartPanelOpen && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: '#666', marginBottom: 8 }}>
                            Скрыто: {Object.values(chartVisibility).filter(v => !v).length} диаграмм
                        </div>
                        <div style={{ display: 'flex', gap: '12px', marginBottom: 12 }}>
                            <button
                                onClick={() => setChartVisibility({ aptsCommentDynamics: true, certStatusDynamics: true, osDynamics: true, labDynamics: true, applicantDynamics: true, manufacturerDynamics: true })}
                                style={{ padding: '4px 12px', fontSize: 13, color: '#16a34a', fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer' }}
                            >Выбрать все</button>
                            <button
                                onClick={() => setChartVisibility({ aptsCommentDynamics: false, certStatusDynamics: false, osDynamics: false, labDynamics: false, applicantDynamics: false, manufacturerDynamics: false })}
                                style={{ padding: '4px 12px', fontSize: 13, color: '#dc2626', fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer' }}
                            >Снять выбор</button>
                        </div>
                        <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: 8 }}>
                        {[
                            { key: 'aptsCommentDynamics', label: 'Комментарий АПТС' },
                            { key: 'certStatusDynamics', label: 'Статус сертификата' },
                            { key: 'osDynamics', label: 'Органы по сертификации' },
                            { key: 'labDynamics', label: 'Лаборатории' },
                            { key: 'applicantDynamics', label: 'Заявители' },
                            { key: 'manufacturerDynamics', label: 'Изготовители' },
                        ].map(({ key, label }) => (
                            <label
                                key={key}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 12px',
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 400,
                                    userSelect: 'none',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                                <input
                                    type="checkbox"
                                    checked={chartVisibility[key as keyof typeof chartVisibility]}
                                    onChange={() => setChartVisibility(prev => ({ ...prev, [key]: !prev[key as keyof typeof chartVisibility] }))}
                                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                        </div>
                    </div>
                    )}
                </div>

                <div className="pdf-page">
                    {chartVisibility.aptsCommentDynamics && (
                    <div className="chart-container__dinamic" style={{ display: 'block', minHeight: 'auto' }}>
                        <p style={{ textAlign: "left", marginLeft: "20px", marginBottom: "0px", fontWeight: "600" }}>Комментарий АПТС - Динамика</p>
                        <div style={{ display: 'flex', gap: 24, marginTop: 12, marginBottom: 8, justifyContent: 'center', flexWrap: 'wrap', padding: '0 20px' }}>
                            {nivoKeys.map((result, idx) => (
                                <div key={result} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: barColors[idx % barColors.length],
                                        marginRight: 6,
                                        border: '1.5px solid #bbb',
                                    }} />
                                    <span style={{ fontWeight: 500 }}>{result}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ width: '100%', height: 450, position: 'relative' }}>
                            <ResponsiveBar
                                data={getNivoBarData()}
                                keys={nivoKeys}
                                indexBy="year"
                                margin={{ top: 50, right: 60, bottom: 80, left: 80 }}
                                padding={0.1}
                                groupMode="stacked"
                                colors={({ id, data }) => {
                                    const idx = nivoKeys.indexOf(String(id));
                                    if (filteredYears.length > 0 && filteredYears.includes(String(data.year))) {
                                        return barColors[idx % barColors.length];
                                    }
                                    if (filteredYears.length > 0) {
                                        return '#eee';
                                    }
                                    return barColors[idx % barColors.length];
                                }}
                                borderRadius={4}
                                enableLabel={true}
                                labelSkipWidth={16}
                                labelSkipHeight={16}
                                labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Год',
                                    legendPosition: 'middle',
                                    legendOffset: 40,
                                    truncateTickAt: 0
                                }}
                                axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Количество сертификатов',
                                    legendPosition: 'middle',
                                     legendOffset: -50,
                                     tickValues: (() => {
                                         const data = getNivoBarData();
                                         const max = data.reduce((maxSoFar, row) => {
                                             const total = nivoKeys.reduce((s, k) => s + Number(row[k] || 0), 0);
                                             return Math.max(maxSoFar, total);
                                         }, 0);
                                         return getIntegerTickValues(max);
                                     })()
                                }}
                                tooltip={({ id, value, color, indexValue, data }) => {
                                    const total = nivoKeys.reduce((sum, key) => sum + Number(data[key] || 0), 0);
                                    const percent = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div
                                            style={{
                                                padding: 12,
                                                background: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: 6,
                                                minWidth: 260,
                                                maxWidth: 340,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                                <strong style={{ color }}>{String(id)}</strong>
                                                <span style={{ fontSize: 13, color: '#888' }}>Год: <b>{String(indexValue)}</b></span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span>Количество:</span>
                                                <b>{String(value)}</b>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span>Процент:</span>
                                                <b>{percent}%</b>
                                            </div>
                                            <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
                                            <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 2 }}>Всего за год: {total}</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, fontSize: 12 }}>
                                                {nivoKeys.map(key => {
                                                    const v = Number(data[key] || 0);
                                                    const p = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
                                                    return (
                                                        <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 2 }}>
                                                            <span style={{ color: barColors[nivoKeys.indexOf(key)], fontWeight: id === key ? 700 : 400 }}>{key}</span>
                                                            <span>{v} ({p}%)</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                }}
                                legends={[]}
                                theme={{
                                    axis: {
                                        legend: {
                                            text: {
                                                fontSize: 16,
                                                fontWeight: 600
                                            }
                                        },
                                        ticks: {
                                            text: {
                                                fontSize: 14
                                            }
                                        }
                                    },
                                    tooltip: {
                                        container: {
                                            fontSize: 14
                                        }
                                    }
                                }}
                                animate={true}
                                motionConfig="gentle"
                                onClick={bar => {
                                    const year = String(bar.data.year);
                                    setFilteredYears(prev =>
                                        prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
                                    );
                                }}
                            />
                        </div>
                    </div>
                    )}
                    {chartVisibility.certStatusDynamics && (
                    <div className="chart-container__dinamic" style={{ display: 'block', minHeight: 'auto' }}>
                        <p style={{ textAlign: "left", marginLeft: "20px", marginBottom: "0px", fontWeight: "600" }}>Статус сертификата в реестре обязательных сертификатов соответствия ФСА - Динамика</p>
                        <div style={{ display: 'flex', gap: 24, marginTop: 12, marginBottom: 8, justifyContent: 'center', flexWrap: 'wrap', padding: '0 20px' }}>
                            {certStatusNivoKeys.map((result, idx) => (
                                <div key={result} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: certStatusNivoColors[idx % certStatusNivoColors.length],
                                        marginRight: 6,
                                        border: '1.5px solid #bbb',
                                    }} />
                                    <span style={{ fontWeight: 500 }}>{result}</span>
                                </div>
                            ))}
                        </div>
                        <div style={{ width: '100%', height: 450, position: 'relative' }}>
                            <ResponsiveBar
                                data={getCertStatusNivoBarData()}
                                keys={certStatusNivoKeys}
                                indexBy="year"
                                margin={{ top: 50, right: 60, bottom: 80, left: 80 }}
                                padding={0.1}
                                groupMode="stacked"
                                colors={({ id, data }) => {
                                    const idx = certStatusNivoKeys.indexOf(String(id));
                                    if (filteredYears.length > 0 && filteredYears.includes(String(data.year))) {
                                        return certStatusNivoColors[idx % certStatusNivoColors.length];
                                    }
                                    if (filteredYears.length > 0) {
                                        return '#eee';
                                    }
                                    return certStatusNivoColors[idx % certStatusNivoColors.length];
                                }}
                                borderRadius={4}
                                enableLabel={true}
                                labelSkipWidth={16}
                                labelSkipHeight={16}
                                labelTextColor="#ffffff"
                                axisBottom={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Год',
                                    legendPosition: 'middle',
                                    legendOffset: 40,
                                    truncateTickAt: 0
                                }}
                                axisLeft={{
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Количество сертификатов',
                                    legendPosition: 'middle',
                                     legendOffset: -50,
                                     tickValues: (() => {
                                         const data = getCertStatusNivoBarData();
                                         const max = data.reduce((maxSoFar, row) => {
                                             const total = certStatusNivoKeys.reduce((s, k) => s + Number(row[k] || 0), 0);
                                             return Math.max(maxSoFar, total);
                                         }, 0);
                                         return getIntegerTickValues(max);
                                     })()
                                }}
                                tooltip={({ id, value, color, indexValue, data }) => {
                                    const total = certStatusNivoKeys.reduce((sum, key) => sum + Number(data[key] || 0), 0);
                                    const percent = total > 0 ? ((Number(value) / total) * 100).toFixed(1) : '0.0';
                                    return (
                                        <div
                                            style={{
                                                padding: 12,
                                                background: '#fff',
                                                border: '1px solid #ccc',
                                                borderRadius: 6,
                                                minWidth: 260,
                                                maxWidth: 340,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 4
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                                <strong style={{ color }}>{String(id)}</strong>
                                                <span style={{ fontSize: 13, color: '#888' }}>Год: <b>{String(indexValue)}</b></span>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span>Количество:</span>
                                                <b>{String(value)}</b>
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                                <span>Процент:</span>
                                                <b>{percent}%</b>
                                            </div>
                                            <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
                                            <div style={{ fontWeight: 500, fontSize: 13, marginBottom: 2 }}>Всего за год: {total}</div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, fontSize: 12 }}>
                                                {certStatusNivoKeys.map(key => {
                                                    const v = Number(data[key] || 0);
                                                    const p = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
                                                    return (
                                                        <div key={key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: 2 }}>
                                                            <span style={{ color: certStatusNivoColors[certStatusNivoKeys.indexOf(key)], fontWeight: id === key ? 700 : 400 }}>{key}</span>
                                                            <span>{v} ({p}%)</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                }}
                                legends={[]}
                                theme={{
                                    axis: {
                                        legend: {
                                            text: {
                                                fontSize: 16,
                                                fontWeight: 600
                                            }
                                        },
                                        ticks: {
                                            text: {
                                                fontSize: 14
                                            }
                                        }
                                    },
                                    tooltip: {
                                        container: {
                                            fontSize: 14
                                        }
                                    }
                                }}
                                animate={true}
                                motionConfig="gentle"
                                onClick={bar => {
                                    const year = String(bar.data.year);
                                    setFilteredYears(prev =>
                                        prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
                                    );
                                }}
                            />
                        </div>
                    </div>
                    )}
                </div>

                <div className="charts-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>

                {(() => {
                    const osData = getOSDynamicsData();
                    const topOS = osData.osNames
                        .sort((a, b) => {
                            const sum = (name: string) => osData.years.reduce((s, y) => s + (osData.osCounts.get(y)?.get(name) || 0), 0);
                            const diff = sum(b) - sum(a);
                            return diff !== 0 ? diff : a.localeCompare(b);
                        }).slice(0, 10);
                    return chartVisibility.osDynamics && (
                        <div className="chart-container__dinamic" style={{ flex: '1 1 calc(50% - 16px)', minWidth: '0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: "20px", marginBottom: "0px" }}>
                                <p style={{ textAlign: "left", margin: 0, fontWeight: "600" }}>Органы по сертификации - Динамика</p>
                                <button
                                    onClick={() => {
                                        try {
                                            const payload = {
                                                filters,
                                                filteredAptsComments,
                                                filteredCertStatuses,
                                                filteredYears,
                                                dateRange: { start: '', end: '' },
                                                endDateRange: { start: '', end: '' },
                                                showTerminatedOnly: false,
                                            };
                                            localStorage.setItem('certificates_dynamics_filters', JSON.stringify(payload));
                                        } catch {}
                                        navigate('/certificates/dynamics/os');
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#1a73e8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        marginRight: '20px',
                                    }}
                                >
                                    Подробнее
                                </button>
                            </div>
                            <div style={{ width: '100%', height: 450, position: 'relative' }}>
                                <ResponsiveBar
                                    data={osData.years.map(year => {
                                        const row: Record<string, number | string> = { year: String(year) };
                                        topOS.forEach(osName => { row[osName] = osData.osCounts.get(year)?.get(osName) || 0; });
                                        return row;
                                    })}
                                    keys={topOS}
                                    tooltip={(d) => {
                                        const idStr = String(d.id);
                                        const total = topOS.reduce((s, k) => s + (Number((d.data as any)[k]) || 0), 0);
                                        const percent = total ? (Number(d.value) as number) / total * 100 : 0;
                                        return (
                                            <div style={{
                                                background: '#fff',
                                                padding: '10px 14px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                maxWidth: '500px',
                                                minWidth: '200px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                                lineHeight: '1.5',
                                            }}>
                                                <div style={{ marginBottom: '6px', color: '#555', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Орган по сертификации</div>
                                                <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#222', wordBreak: 'break-word' }}>{idStr}</div>
                                                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                                                    <span><strong style={{ color: '#333' }}>Год:</strong> {d.data.year}</span>
                                                    <span><strong style={{ color: '#333' }}>Кол-во:</strong> <span style={{ fontWeight: '600', color: '#1a73e8' }}>{d.value} ({percent.toFixed(1)}%)</span></span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                    indexBy="year"
                                    margin={{ top: 50, right: 24, bottom: 80, left: 80 }}
                                    padding={0.1}
                                    groupMode="stacked"
                                    colors={({ id, data }) => {
                                        const idx = topOS.indexOf(String(id));
                                        if (filteredYears.length > 0 && filteredYears.includes(String(data.year))) return seriesColors[idx % seriesColors.length];
                                        if (filteredYears.length > 0) return '#eee';
                                        return seriesColors[idx % seriesColors.length];
                                    }}
                                    borderRadius={4}
                                    enableLabel={true}
                                    labelSkipWidth={16}
                                    labelSkipHeight={16}
                                    labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                    axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Год', legendPosition: 'middle', legendOffset: 40, truncateTickAt: 0 }}
                                    axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Количество сертификатов', legendPosition: 'middle', legendOffset: -50 }}
                                    legends={[]}
                                    animate={true}
                                    motionConfig="gentle"
                                    onClick={bar => {
                                        const year = String(bar.data.year);
                                        setFilteredYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]);
                                    }}
                                />
                            </div>
                        </div>
                    );
                })()}

                {(() => {
                    const labData = getLabDynamicsData();
                    const topLabs = labData.labNames
                        .sort((a, b) => {
                            const sum = (name: string) => labData.years.reduce((s, y) => s + (labData.labCounts.get(y)?.get(name) || 0), 0);
                            const diff = sum(b) - sum(a);
                            return diff !== 0 ? diff : a.localeCompare(b);
                        }).slice(0, 10);
                    return chartVisibility.labDynamics && (
                        <div className="chart-container__dinamic" style={{ flex: '1 1 calc(50% - 16px)', minWidth: '0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: "20px", marginBottom: "0px" }}>
                                <p style={{ textAlign: "left", margin: 0, fontWeight: "600" }}>Лаборатории - Динамика</p>
                                <button
                                    onClick={() => {
                                        try {
                                            const payload = {
                                                filters,
                                                filteredAptsComments,
                                                filteredCertStatuses,
                                                filteredYears,
                                                dateRange: { start: '', end: '' },
                                                endDateRange: { start: '', end: '' },
                                                showTerminatedOnly: false,
                                            };
                                            localStorage.setItem('certificates_dynamics_filters', JSON.stringify(payload));
                                        } catch {}
                                        navigate('/certificates/dynamics/labs');
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#1a73e8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        marginRight: '20px',
                                    }}
                                >
                                    Подробнее
                                </button>
                            </div>
                            <div style={{ width: '100%', height: 450, position: 'relative' }}>
                                <ResponsiveBar
                                    data={labData.years.map(year => {
                                        const row: Record<string, number | string> = { year: String(year) };
                                        topLabs.forEach(name => { row[name] = labData.labCounts.get(year)?.get(name) || 0; });
                                        return row;
                                    })}
                                    keys={topLabs}
                                    tooltip={(d) => {
                                        const idStr = String(d.id);
                                        const total = topLabs.reduce((s, k) => s + (Number((d.data as any)[k]) || 0), 0);
                                        const percent = total ? (Number(d.value) as number) / total * 100 : 0;
                                        return (
                                            <div style={{
                                                background: '#fff',
                                                padding: '10px 14px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                maxWidth: '500px',
                                                minWidth: '200px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                                lineHeight: '1.5',
                                            }}>
                                                <div style={{ marginBottom: '6px', color: '#555', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Лаборатория</div>
                                                <div style={{ marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#222', wordBreak: 'break-word' }}>{idStr}</div>
                                                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                                                    <span><strong style={{ color: '#333' }}>Год:</strong> {d.data.year}</span>
                                                    <span><strong style={{ color: '#333' }}>Кол-во:</strong> <span style={{ fontWeight: '600', color: '#1a73e8' }}>{d.value} ({percent.toFixed(1)}%)</span></span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                    indexBy="year"
                                    margin={{ top: 50, right: 24, bottom: 80, left: 80 }}
                                    padding={0.1}
                                    groupMode="stacked"
                                    colors={({ id, data }) => {
                                        const idx = topLabs.indexOf(String(id));
                                        if (filteredYears.length > 0 && filteredYears.includes(String(data.year))) return seriesColors[idx % seriesColors.length];
                                        if (filteredYears.length > 0) return '#eee';
                                        return seriesColors[idx % seriesColors.length];
                                    }}
                                    borderRadius={4}
                                    enableLabel={true}
                                    labelSkipWidth={16}
                                    labelSkipHeight={16}
                                    labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                    axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Год', legendPosition: 'middle', legendOffset: 40, truncateTickAt: 0 }}
                                    axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Количество сертификатов', legendPosition: 'middle', legendOffset: -50 }}
                                    legends={[]}
                                    animate={true}
                                    motionConfig="gentle"
                                    onClick={bar => {
                                        const year = String(bar.data.year);
                                        setFilteredYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]);
                                    }}
                                />
                            </div>
                        </div>
                    );
                })()}

                {(() => {
                    const applicantData = getApplicantDynamicsData();
                    const topApplicants = applicantData.applicantNames
                        .sort((a, b) => {
                            const sum = (name: string) => applicantData.years.reduce((s, y) => s + (applicantData.applicantCounts.get(y)?.get(name) || 0), 0);
                            const diff = sum(b) - sum(a);
                            return diff !== 0 ? diff : a.localeCompare(b);
                        }).slice(0, 10);
                    return chartVisibility.applicantDynamics && (
                        <div className="chart-container__dinamic" style={{ flex: '1 1 calc(50% - 16px)', minWidth: '0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: "20px", marginBottom: "0px" }}>
                                <p style={{ textAlign: "left", margin: 0, fontWeight: "600" }}>Заявители - Динамика</p>
                                <button
                                    onClick={() => {
                                        try {
                                            const payload = {
                                                filters,
                                                filteredAptsComments,
                                                filteredCertStatuses,
                                                filteredYears,
                                                dateRange: { start: '', end: '' },
                                                endDateRange: { start: '', end: '' },
                                                showTerminatedOnly: false,
                                            };
                                            localStorage.setItem('certificates_dynamics_filters', JSON.stringify(payload));
                                        } catch {}
                                        navigate('/certificates/dynamics/applicants');
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#1a73e8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        marginRight: '20px',
                                    }}
                                >
                                    Подробнее
                                </button>
                            </div>
                            <div style={{ width: '100%', height: 450, position: 'relative' }}>
                                <ResponsiveBar
                                    data={applicantData.years.map(year => {
                                        const row: Record<string, number | string> = { year: String(year) };
                                        topApplicants.forEach(name => {
                                            const count = applicantData.applicantCounts.get(year)?.get(name) || 0;
                                            row[name] = count;
                                        });
                                        return row;
                                    })}
                                    keys={topApplicants}
                                    tooltip={(d) => {
                                        const idStr = String(d.id);
                                        const innNameMatch = idStr.match(/^(\d{10,})\s*-\s*(.+)$/);
                                        let displayName: string = '';
                                        let inn: string = '';
                                        if (innNameMatch) {
                                            inn = innNameMatch[1];
                                            displayName = innNameMatch[2];
                                        } else {
                                            const isInn = /^\d{10,}$/.test(idStr);
                                            displayName = isInn ? (applicantMaps.innToName.get(idStr) || idStr) : idStr;
                                            inn = isInn ? idStr : (applicantMaps.nameToInn.get(idStr) || '');
                                        }
                                        const total = topApplicants.reduce((s, k) => s + (Number((d.data as any)[k]) || 0), 0);
                                        const percent = total ? (Number(d.value) as number) / total * 100 : 0;
                                        return (
                                            <div style={{
                                                background: '#fff',
                                                padding: '10px 14px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                maxWidth: '500px',
                                                minWidth: '200px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                                lineHeight: '1.5',
                                            }}>
                                                <div style={{ marginBottom: '6px', color: '#555', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Заявитель</div>
                                                <div style={{ marginBottom: '4px', fontSize: '14px', fontWeight: '600', color: '#222', wordBreak: 'break-word' }}>{displayName}</div>
                                                {inn && (
                                                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#888' }}>ИНН: {inn}</div>
                                                )}
                                                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                                                    <span><strong style={{ color: '#333' }}>Год:</strong> {d.data.year}</span>
                                                    <span><strong style={{ color: '#333' }}>Кол-во:</strong> <span style={{ fontWeight: '600', color: '#1a73e8' }}>{d.value} ({percent.toFixed(1)}%)</span></span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                    indexBy="year"
                                    margin={{ top: 50, right: 24, bottom: 80, left: 80 }}
                                    padding={0.1}
                                    groupMode="stacked"
                                    colors={({ id, data }) => {
                                        const idx = topApplicants.indexOf(String(id));
                                        if (filteredYears.length > 0 && filteredYears.includes(String(data.year))) return seriesColors[idx % seriesColors.length];
                                        if (filteredYears.length > 0) return '#eee';
                                        return seriesColors[idx % seriesColors.length];
                                    }}
                                    borderRadius={4}
                                    enableLabel={true}
                                    labelSkipWidth={16}
                                    labelSkipHeight={16}
                                    labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                    axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Год', legendPosition: 'middle', legendOffset: 40, truncateTickAt: 0 }}
                                    axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Количество сертификатов', legendPosition: 'middle', legendOffset: -50 }}
                                    legends={[]}
                                    animate={true}
                                    motionConfig="gentle"
                                    onClick={bar => {
                                        const year = String(bar.data.year);
                                        setFilteredYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]);
                                    }}
                                />
                            </div>
                        </div>
                    );
                })()}

                {(() => {
                    const manufacturerData = getManufacturerDynamicsData();
                    const topManufacturers = manufacturerData.manufacturerNames
                        .sort((a, b) => {
                            const sum = (name: string) => manufacturerData.years.reduce((s, y) => s + (manufacturerData.manufacturerCounts.get(y)?.get(name) || 0), 0);
                            const diff = sum(b) - sum(a);
                            return diff !== 0 ? diff : a.localeCompare(b);
                        }).slice(0, 10);
                    return chartVisibility.manufacturerDynamics && (
                        <div className="chart-container__dinamic" style={{ flex: '1 1 calc(50% - 16px)', minWidth: '0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginLeft: "20px", marginBottom: "0px" }}>
                                <p style={{ textAlign: "left", margin: 0, fontWeight: "600" }}>Изготовители - Динамика</p>
                                <button
                                    onClick={() => {
                                        try {
                                            const payload = {
                                                filters,
                                                filteredAptsComments,
                                                filteredCertStatuses,
                                                filteredYears,
                                                dateRange: { start: '', end: '' },
                                                endDateRange: { start: '', end: '' },
                                                showTerminatedOnly: false,
                                            };
                                            localStorage.setItem('certificates_dynamics_filters', JSON.stringify(payload));
                                        } catch {}
                                        navigate('/certificates/dynamics/manufacturers');
                                    }}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: '#1a73e8',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        marginRight: '20px',
                                    }}
                                >
                                    Подробнее
                                </button>
                            </div>
                            <div style={{ width: '100%', height: 450, position: 'relative' }}>
                                <ResponsiveBar
                                    data={manufacturerData.years.map(year => {
                                        const row: Record<string, number | string> = { year: String(year) };
                                        topManufacturers.forEach(name => { row[name] = manufacturerData.manufacturerCounts.get(year)?.get(name) || 0; });
                                        return row;
                                    })}
                                    keys={topManufacturers}
                                    tooltip={(d) => {
                                        const idStr = String(d.id);
                                        const isInn = /^\d{10,}$/.test(idStr);
                                        const displayName = isInn ? (manufacturerMaps.innToName.get(idStr) || idStr) : idStr;
                                        const inn = isInn ? idStr : (manufacturerMaps.nameToInn.get(idStr) || '');
                                        const total = topManufacturers.reduce((s, k) => s + (Number((d.data as any)[k]) || 0), 0);
                                        const percent = total ? (Number(d.value) as number) / total * 100 : 0;
                                        return (
                                            <div style={{
                                                background: '#fff',
                                                padding: '10px 14px',
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                fontSize: '13px',
                                                maxWidth: '500px',
                                                minWidth: '200px',
                                                wordWrap: 'break-word',
                                                overflowWrap: 'break-word',
                                                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                                lineHeight: '1.5',
                                            }}>
                                                <div style={{ marginBottom: '6px', color: '#555', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Изготовитель</div>
                                                <div style={{ marginBottom: '4px', fontSize: '14px', fontWeight: '600', color: '#222', wordBreak: 'break-word' }}>{displayName}</div>
                                                {inn && inn !== displayName && (
                                                    <div style={{ marginBottom: '8px', fontSize: '12px', color: '#888' }}>ИНН: {inn}</div>
                                                )}
                                                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                                                    <span><strong style={{ color: '#333' }}>Год:</strong> {d.data.year}</span>
                                                    <span><strong style={{ color: '#333' }}>Кол-во:</strong> <span style={{ fontWeight: '600', color: '#1a73e8' }}>{d.value} ({percent.toFixed(1)}%)</span></span>
                                                </div>
                                            </div>
                                        );
                                    }}
                                    indexBy="year"
                                    margin={{ top: 50, right: 24, bottom: 80, left: 80 }}
                                    padding={0.1}
                                    groupMode="stacked"
                                    colors={({ id, data }) => {
                                        const idx = topManufacturers.indexOf(String(id));
                                        if (filteredYears.length > 0 && filteredYears.includes(String(data.year))) return seriesColors[idx % seriesColors.length];
                                        if (filteredYears.length > 0) return '#eee';
                                        return seriesColors[idx % seriesColors.length];
                                    }}
                                    borderRadius={4}
                                    enableLabel={true}
                                    labelSkipWidth={16}
                                    labelSkipHeight={16}
                                    labelTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                                    axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Год', legendPosition: 'middle', legendOffset: 40, truncateTickAt: 0 }}
                                    axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legend: 'Количество сертификатов', legendPosition: 'middle', legendOffset: -50 }}
                                    legends={[]}
                                    animate={true}
                                    motionConfig="gentle"
                                    onClick={bar => {
                                        const year = String(bar.data.year);
                                        setFilteredYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]);
                                    }}
                                />
                            </div>
                        </div>
                    );
                })()}

                </div>

                <div className="pdf-page">
                    <div className="pdf-table">
                        <div className="certification-bodies-table">
                            <p>Список органов по сертификации</p>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Номер</th>
                                        <th>Наименование</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getUniqueCertificationBodies().map((body, index) => (
                                        <tr key={index}>
                                            <td>{body.number}</td>
                                            <td>{body.name}</td>
                                            <td>{body.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="laboratories-table">
                            <p>Список лабораторий</p>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Номер</th>
                                        <th>Наименование</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getUniqueLaboratories().map((lab, index) => (
                                        <tr key={index}>
                                            <td>{lab.number}</td>
                                            <td>{lab.name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="pdf-page">
                    <div className="certificates-table">
                        <p>Реестр обязательных сертификатов</p>
                        <div className="items-per-page">
                            <label htmlFor="itemsPerPage">Количество на странице:</label>
                            <select id="itemsPerPage" value={itemsPerPage} onChange={handleItemsPerPageChange}>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={200}>200</option>
                                <option value={filteredCertif.length}>Все</option>
                            </select>
                        </div>
                        <Certificates
                            filteredLabList={currentItems}
                            onSort={handleSort}
                            getSortIcon={getSortIcon}
                            onRowClick={handleOpenCertDetails}
                        />
                        <UniversalPagination<Certif>
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            paginate={paginate}
                            items={filteredCertif}
                        />
                    </div>
                </div>
            </div>
            )}
        </div>
    );
}

export default CertificatesPage;